import React, { Suspense, useRef } from "react";
import { Canvas, useLoader, useThree } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import * as THREE from "three";
import "./InteractiveGlobe.css";
import earthTexture from "../../../assets/images/simulator/earth.webp";

const EARTH_TEXTURE = earthTexture;

const EARTH_NORMAL =
  "https://threejs.org/examples/textures/planets/earth_normal_2048.jpg";

const EARTH_SPECULAR =
  "https://threejs.org/examples/textures/planets/earth_specular_2048.jpg";

const Globe = () => {
  const meshRef = useRef();

  const [colorMap, normalMap, specularMap] = useLoader(
    THREE.TextureLoader,
    [EARTH_TEXTURE, EARTH_NORMAL, EARTH_SPECULAR]
  );

  return (
    <mesh ref={meshRef} rotation={[0, -0.5, 0]}>
      <sphereGeometry args={[2.15, 64, 64]} />
      <meshPhongMaterial
        map={colorMap}
        normalMap={normalMap}
        specularMap={specularMap}
        specular={new THREE.Color("#333333")}
        shininess={8}
      />
    </mesh>
  );
};

const ResponsiveCamera = () => {
  const { camera, size } = useThree();

  React.useEffect(() => {
    const aspect = size.width / size.height;

    const baseDistance = 6;
    const responsiveDistance =
      aspect < 0.8
        ? 6.8
        : aspect < 1
        ? 6.4
        : baseDistance;

    camera.position.set(0, 0, responsiveDistance);
    camera.fov = aspect < 0.8 ? 50 : 45;
    camera.updateProjectionMatrix();
  }, [camera, size.width, size.height]);

  return null;
};

const GlobeLoader = () => {
  return (
    <Canvas
      camera={{
        position: [0, 0, 6],
        fov: 45,
        near: 0.1,
        far: 100,
      }}
      dpr={[1, 1.5]}
      resize={{
        scroll: false,
        debounce: {
          scroll: 0,
          resize: 100,
        },
      }}
      gl={{
        antialias: true,
        alpha: true,
      }}
    >
      <ResponsiveCamera />

      <ambientLight intensity={1.2} />

      <directionalLight
        position={[5, 3, 5]}
        intensity={2}
      />

      <directionalLight
        position={[-4, -2, -3]}
        intensity={0.5}
      />

      <Suspense fallback={null}>
        <Globe />
      </Suspense>

      <OrbitControls
        enablePan={false}
        enableZoom={true}
        minDistance={6}
        maxDistance={8}
        rotateSpeed={0.5}
        makeDefault
        minAzimuthAngle={-Math.PI / 12}
        maxAzimuthAngle={Math.PI / 12}
        minPolarAngle={Math.PI / 3}
        maxPolarAngle={Math.PI * 2 / 3}
      />
    </Canvas>
  );
};

const InteractiveGlobe = () => {
  return (
    <div className="interactive-globe">
      <GlobeLoader />
    </div>
  );
};

export default InteractiveGlobe;